import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';
import { BookingStatus } from '@/types';
import { useGuideBookings } from '@/features/guide/hooks/useGuideBookings';
import { BookingCard } from '@/features/guide/components/BookingCard';
import { BookingDetailDrawer } from '@/features/guide/components/BookingDetailDrawer';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';

type PastTab = 'completed' | 'cancelled' | 'refunded';

const TAB_STATUS: Record<PastTab, string> = {
  completed: BookingStatus.COMPLETED,
  cancelled: BookingStatus.CANCELLED,
  refunded: BookingStatus.REFUNDED,
};

const TAB_SORT: Record<PastTab, string> = {
  completed: 'completedAt',
  cancelled: 'cancelledAt',
  refunded: 'updatedAt',
};

export function GuidePastBookingsPage() {
  const [tab, setTab] = useState<PastTab>('completed');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const status = TAB_STATUS[tab];
  const { data, isLoading, error } = useGuideBookings({
    status,
    sortBy: TAB_SORT[tab],
    page: page + 1,
    limit: rowsPerPage,
  });

  const bookings = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <GuidePageHeader
        title="Past trips"
        subtitle="Review completed and closed bookings with full trip details."
      />

      <Tabs
        value={tab}
        onChange={(_, value: PastTab) => {
          setTab(value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Completed" value="completed" />
        <Tab label="Cancelled" value="cancelled" disabled />
        <Tab label="Refunded" value="refunded" disabled />
      </Tabs>

      {tab !== 'completed' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cancelled and refunded trips will appear here in a future update.
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load past bookings. Please try again.
        </Alert>
      )}

      {!isLoading && !error && bookings.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            No past trips yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completed bookings will show up here after travelers confirm trip completion.
          </Typography>
        </Paper>
      )}

      {bookings.map((booking) => (
        <BookingCard key={booking.id} booking={booking} onClick={() => setSelectedId(booking.id)} />
      ))}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      )}

      <BookingDetailDrawer bookingId={selectedId} onClose={() => setSelectedId(null)} readOnly />
    </Box>
  );
}
